import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_FX, FX_STYLES, normalizeFx, type DesignFx, type FxStyle } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import './panels.css';

const STYLE_LABELS: Record<FxStyle, string> = {
  dots: 'Dust',
  stars: 'Starfield',
  snow: 'Snowfall',
  bubbles: 'Bubbles',
  embers: 'Embers',
  confetti: 'Confetti',
  network: 'Network',
  fireflies: 'Fireflies',
  rain: 'Rain'
};

const PRESET_ORDER: FxStyle[] = ['snow', 'fireflies', 'stars', 'bubbles', 'embers', 'confetti', 'network', 'rain', 'dots'];

const PRESETS: Record<FxStyle, { count: number; speed: number; size: number; opacity: number; colors: string[] }> = {
  snow: { count: 120, speed: 1, size: 4, opacity: 0.8, colors: ['#ffffff'] },
  fireflies: { count: 35, speed: 1.2, size: 3, opacity: 0.8, colors: ['#fbbf24'] },
  stars: { count: 170, speed: 0.6, size: 2, opacity: 0.9, colors: ['#ffffff'] },
  bubbles: { count: 50, speed: 0.8, size: 6, opacity: 0.5, colors: ['#7dd3fc', '#e0f2fe'] },
  embers: { count: 100, speed: 1.1, size: 3, opacity: 0.8, colors: ['#fb923c', '#f87171'] },
  confetti: { count: 120, speed: 1.4, size: 5, opacity: 0.9, colors: ['#f472b6', '#facc15', '#4ade80', '#60a5fa'] },
  network: { count: 90, speed: 1, size: 2, opacity: 0.6, colors: ['#93c5fd'] },
  rain: { count: 130, speed: 1.3, size: 2, opacity: 0.5, colors: ['#93c5fd'] },
  dots: { count: 80, speed: 1, size: 3, opacity: 0.6, colors: ['#c7d2fe'] }
};

interface EffectsPanelProps {
  ctx: PanelCtx;
}

function cloneFx(fx: DesignFx): DesignFx {
  return { ...fx, colors: [...fx.colors] };
}

export default function EffectsPanel({ ctx }: EffectsPanelProps) {
  const [fx, setFx] = useState<DesignFx>(() => cloneFx(ctx.props.fx ?? DEFAULT_FX));
  const debounceTimer = useRef<number | null>(null);
  const latestFx = useRef<DesignFx | null>(null);

  useEffect(() => {
    const source = ctx.props.fx;
    if (!source) return;
    setFx((prev) => {
      if (
        prev.mode === source.mode &&
        prev.style === source.style &&
        prev.count === source.count &&
        prev.speed === source.speed &&
        prev.size === source.size &&
        prev.opacity === source.opacity &&
        prev.interact === source.interact &&
        prev.colors.length === source.colors.length &&
        prev.colors.every((color, index) => color === source.colors[index])
      ) {
        return prev;
      }
      return cloneFx(source);
    });
  }, [ctx.props.fx]);

  const scheduleUpdate = useCallback((next: DesignFx) => {
    latestFx.current = next;
    if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      if (latestFx.current) ctx.update({ fx: latestFx.current });
      debounceTimer.current = null;
      latestFx.current = null;
    }, 250);
  }, [ctx.update]);

  useEffect(() => () => {
    if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
  }, []);

  const commit = useCallback((next: DesignFx) => {
    setFx(next);
    ctx.post({ type: 'fx', fx: next });
    scheduleUpdate(next);
  }, [ctx.post, scheduleUpdate]);

  const setMode = useCallback((mode: DesignFx['mode']) => {
    commit(normalizeFx({ ...fx, mode }));
  }, [commit, fx]);

  const applyPreset = useCallback((style: FxStyle) => {
    const preset = PRESETS[style];
    commit(normalizeFx({
      ...fx,
      mode: 'custom',
      style,
      count: preset.count,
      speed: preset.speed,
      size: preset.size,
      opacity: preset.opacity,
      colors: preset.colors
    }));
  }, [commit, fx]);

  const handleSlider = useCallback((field: 'count' | 'speed' | 'size' | 'opacity', raw: string) => {
    const value = Number(raw);
    if (Number.isNaN(value)) return;
    const patch = field === 'opacity' ? { opacity: value / 100 } : { [field]: value };
    commit(normalizeFx({ ...fx, ...patch }));
  }, [commit, fx]);

  const changeColor = useCallback((index: number, color: string) => {
    const colors = [...fx.colors];
    colors[index] = color;
    commit(normalizeFx({ ...fx, colors }));
  }, [commit, fx]);

  const removeColor = useCallback((index: number) => {
    if (fx.colors.length <= 1) return;
    const colors = fx.colors.filter((_, colorIndex) => colorIndex !== index);
    commit(normalizeFx({ ...fx, colors }));
  }, [commit, fx]);

  const addColor = useCallback(() => {
    if (fx.colors.length >= 4) return;
    commit(normalizeFx({ ...fx, colors: [...fx.colors, '#ffffff'] }));
  }, [commit, fx]);

  const toggleInteract = useCallback(() => {
    commit(normalizeFx({ ...fx, interact: !fx.interact }));
  }, [commit, fx]);

  return (
    <div className="dwp-panel">
      <h2 className="dwp-heading">Animated background</h2>

      <div className="dwp-segment" role="radiogroup" aria-label="Animated background mode">
        {(['original', 'none', 'custom'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={fx.mode === mode}
            className={`dwp-segment-btn${fx.mode === mode ? ' is-active' : ''}`}
            onClick={() => setMode(mode)}
          >
            {mode === 'original' ? 'Original' : mode === 'none' ? 'Off' : 'Custom'}
          </button>
        ))}
      </div>

      <p className="dwp-help">
        {fx.mode === 'original'
          ? 'Keep the design’s own animated background.'
          : fx.mode === 'none'
          ? 'Remove the animated background entirely.'
          : 'Choose a custom particle effect and tune it below.'}
      </p>

      {fx.mode === 'custom' && (
        <>
          <div className="dwp-field">
            <span className="dwp-label">Presets</span>
            <div className="dwp-presets" role="group" aria-label="Effect presets">
              {PRESET_ORDER.map((style) => (
                <button
                  key={style}
                  type="button"
                  className={`dwp-chip${fx.style === style ? ' is-active' : ''}`}
                  onClick={() => applyPreset(style)}
                >
                  {STYLE_LABELS[style]}
                </button>
              ))}
            </div>
          </div>

          <div className="dwp-field">
            <label className="dwp-label" htmlFor="dwp-fx-style">Style</label>
            <select
              id="dwp-fx-style"
              className="dwp-select"
              value={fx.style}
              onChange={(event) => {
                const style = event.target.value as FxStyle;
                commit(normalizeFx({ ...fx, style, mode: 'custom' }));
              }}
            >
              {FX_STYLES.map((style) => (
                <option key={style} value={style}>{STYLE_LABELS[style]}</option>
              ))}
            </select>
          </div>

          <div className="dwp-sliders">
            <div className="dwp-slider-row">
              <label className="dwp-label" htmlFor="dwp-fx-count">Amount</label>
              <input
                id="dwp-fx-count"
                type="range"
                min={0}
                max={300}
                step={1}
                value={fx.count}
                onChange={(event) => handleSlider('count', event.target.value)}
              />
              <span className="dwp-readout" aria-live="polite">{fx.count}</span>
            </div>
            <div className="dwp-slider-row">
              <label className="dwp-label" htmlFor="dwp-fx-speed">Speed</label>
              <input
                id="dwp-fx-speed"
                type="range"
                min={0.1}
                max={5}
                step={0.1}
                value={fx.speed}
                onChange={(event) => handleSlider('speed', event.target.value)}
              />
              <span className="dwp-readout" aria-live="polite">{fx.speed.toFixed(1)}</span>
            </div>
            <div className="dwp-slider-row">
              <label className="dwp-label" htmlFor="dwp-fx-size">Size</label>
              <input
                id="dwp-fx-size"
                type="range"
                min={1}
                max={30}
                step={1}
                value={fx.size}
                onChange={(event) => handleSlider('size', event.target.value)}
              />
              <span className="dwp-readout" aria-live="polite">{fx.size}</span>
            </div>
            <div className="dwp-slider-row">
              <label className="dwp-label" htmlFor="dwp-fx-opacity">Opacity</label>
              <input
                id="dwp-fx-opacity"
                type="range"
                min={5}
                max={100}
                step={1}
                value={Math.round(fx.opacity * 100)}
                onChange={(event) => handleSlider('opacity', event.target.value)}
              />
              <span className="dwp-readout" aria-live="polite">{Math.round(fx.opacity * 100)}%</span>
            </div>
          </div>

          <div className="dwp-field">
            <span className="dwp-label">Colours</span>
            <div className="dwp-color-list">
              {fx.colors.map((color, index) => (
                <div className="dwp-color-item" key={`${index}-${color}`}>
                  <input
                    type="color"
                    className="dwp-color-input"
                    value={color}
                    aria-label={`Colour ${index + 1}`}
                    onChange={(event) => changeColor(index, event.target.value)}
                  />
                  <button
                    type="button"
                    className="dwp-icon-btn"
                    aria-label={`Remove colour ${index + 1}`}
                    disabled={fx.colors.length <= 1}
                    onClick={() => removeColor(index)}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="dwp-add-color"
                disabled={fx.colors.length >= 4}
                onClick={addColor}
              >
                <Icon name="plus" size={14} />
                <span>Add colour</span>
              </button>
            </div>
          </div>

          <label className="dwp-check">
            <input type="checkbox" checked={fx.interact} onChange={toggleInteract} />
            <span>React to mouse</span>
          </label>
        </>
      )}
    </div>
  );
}
